// Pavé numérique de connexion du chauffeur: NIP de 6 chiffres, gros boutons (gants, soleil).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Delete, Loader2, ArrowLeft } from "lucide-react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "effacer", "0", "ok"];

export default function PinPad({ onBack }) {
  const { loginPin } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (value) => {
    setLoading(true); setErr("");
    try {
      await loginPin(value);
      nav("/driver");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || "Connexion impossible");
      setPin("");
      if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
    } finally { setLoading(false); }
  }, [loginPin, nav]);

  const press = (k) => {
    if (loading) return;
    if (k === "effacer") return setPin((p) => p.slice(0, -1));
    if (k === "ok") return pin.length === 6 ? submit(pin) : setErr("Entrez vos 6 chiffres.");
    if (pin.length >= 6) return;
    const next = pin + k;
    setPin(next);
    setErr("");
    if (next.length === 6) submit(next);          // validation automatique
  };

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("effacer");
      else if (e.key === "Enter") press("ok");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  return (
    <div className="w-full max-w-xs" data-testid="pin-pad">
      <button onClick={onBack} data-testid="pin-back"
        className="text-xs text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground">
        <ArrowLeft className="w-3.5 h-3.5" /> Accès répartiteur / administrateur
      </button>

      <div className="overline mb-2">Accès chauffeur</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1">Entrez votre NIP</h1>
      <p className="text-sm text-muted-foreground mb-6">6 chiffres remis par votre répartiteur.</p>

      <div className="flex justify-center gap-2.5 mb-5" data-testid="pin-dots">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`w-4 h-4 rounded-full transition-all ${
            i < pin.length ? "bg-primary scale-110" : "bg-secondary border border-border"}`} />
        ))}
      </div>

      {err && <div className="mb-4 text-sm text-center text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
        data-testid="pin-error">{err}</div>}

      <div className="grid grid-cols-3 gap-2.5">
        {KEYS.map((k) => (
          <button key={k} onClick={() => press(k)} disabled={loading}
            data-testid={`pin-key-${k}`}
            className={`h-16 rounded-xl font-display text-2xl font-bold transition-all active:scale-95 disabled:opacity-50
              ${k === "ok" ? "bg-primary text-primary-foreground text-base"
                : k === "effacer" ? "bg-secondary text-slate-500" : "bg-secondary hover:bg-border"}`}>
            {k === "effacer" ? <Delete className="w-6 h-6 mx-auto" />
              : k === "ok" ? (loading ? <Loader2 className="w-5 h-5 mx-auto animate-spin" /> : "ENTRER")
              : k}
          </button>
        ))}
      </div>
    </div>
  );
}
